import {
	CalendarOutlined,
	CreditCardOutlined,
	DeleteOutlined,
	EditOutlined,
	EllipsisOutlined,
	EyeOutlined,
	GiftOutlined,
	ScheduleOutlined,
	StarOutlined,
} from '@ant-design/icons'
import type { Member, MemberAttribute } from '@di-devs/member-feature-plugin/shared'
import {
	Button,
	Collapse,
	Descriptions,
	Divider,
	Dropdown,
	Empty,
	Image,
	Modal,
	Table,
	Tag,
} from 'antd'
import { motion } from 'framer-motion'
import moment from 'moment'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { TapAnimationComponent } from '@/components'
import { API, COMMONS, COUPON_UTILS } from '@/utils'

const MemberActionsDiv = styled.div`
	.ant-dropdown-trigger {
		font-size: 18px;
		margin-right: 1.5em;
		position: relative;
		top: -4px;
	}
`

const StatusBadge = styled.span<{ $status?: string }>`
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	border-radius: 20px;
	font-size: 14px;
	font-weight: 500;
	${({ $status }) => {
		switch ($status) {
			case 'active':
				return 'background: #D1FAE5; color: #065F46;'
			case 'provisional':
				return 'background: #FEF3C7; color: #92400E;'
			case 'expired':
				return 'background: #FEE2E2; color: #991B1B;'
			case 'deactivated':
				return 'background: #F3F4F6; color: #6B7280;'
			default:
				return 'background: #F3F4F6; color: #6B7280;'
		}
	}}
`

const getStatusLabel = (status: string | null) => {
	switch (status) {
		case 'active':
			return '有効'
		case 'provisional':
			return '仮登録'
		case 'expired':
			return '期限切れ'
		case 'deactivated':
			return '無効'
		default:
			return '不明'
	}
}

const getPaymentMethodLabel = (method: string | null) => {
	switch (method) {
		case 'card':
			return 'クレジットカード'
		case 'on_site':
			return '現地払い'
		default:
			return method || 'ー'
	}
}

const getPaymentStatusColor = (status: string) => {
	switch (status) {
		case 'completed':
			return 'success'
		case 'pending':
			return 'warning'
		case 'failed':
			return 'error'
		case 'refunded':
			return 'default'
		default:
			return 'default'
	}
}

const getPaymentStatusLabel = (status: string) => {
	switch (status) {
		case 'completed':
			return '完了'
		case 'pending':
			return '保留中'
		case 'failed':
			return '失敗'
		case 'refunded':
			return '返金済み'
		default:
			return status || 'ー'
	}
}

const getPointSourceLabel = (source: string | null) => {
	switch (source) {
		case 'visit':
			return '来店'
		case 'registration':
			return '新規登録'
		case 'renewal':
			return '更新'
		case 'event':
			return '販売会・イベントP'
		case 'admin':
			return '手動付与'
		case 'birthday':
			return '誕生日特典'
		case 'campaign':
			return 'キャンペーン'
		case 'migration':
			return 'データ移行'
		default:
			return source || 'ー'
	}
}

const getEventRegistrationStatusColor = (status: string | null) => {
	switch (status) {
		case 'confirmed':
			return 'success'
		case 'pending':
			return 'warning'
		case 'cancelled':
			return 'error'
		case 'no_show':
			return 'default'
		default:
			return 'default'
	}
}

const getEventRegistrationStatusLabel = (status: string | null) => {
	switch (status) {
		case 'confirmed':
			return '予約済'
		case 'pending':
			return '仮予約'
		case 'cancelled':
			return 'キャンセル'
		case 'no_show':
			return '無断キャンセル'
		default:
			return status || 'ー'
	}
}

const getEventPaymentStatusColor = (status: string | null) => {
	switch (status) {
		case 'completed':
			return 'success'
		case 'free':
			return 'default'
		case 'unpaid':
			return 'error'
		default:
			return 'default'
	}
}

const getEventPaymentStatusLabel = (status: string | null) => {
	switch (status) {
		case 'completed':
			return '支払済'
		case 'free':
			return '無料'
		case 'unpaid':
			return '支払前'
		default:
			return status || 'ー'
	}
}

const getCheckInStatusColor = (checkedIn: boolean, endAt: string | null) => {
	if (checkedIn) return 'success'
	if (endAt && moment(endAt).isBefore(moment())) return 'default'
	return 'error'
}

const getCheckInStatusLabel = (checkedIn: boolean, endAt: string | null) => {
	if (checkedIn) return '完了'
	if (endAt && moment(endAt).isBefore(moment())) return '欠席'
	return '未完了'
}

const formatJstDate = (date: string | Date | null | undefined, format = 'YYYY年M月D日') => {
	if (!date) {
		return 'ー'
	}
	return moment(date).utcOffset(9).format(format)
}

type PointRecord = {
	pointId: number
	point: number
	remainingPoints: number | null
	expiresAt: string | null
	source: string | null
	processedAt: string | null
	createdAt: string | null
	updatedAt: string | null
}

type CouponRecord = {
	couponId: number
	couponMasterId: number
	issuedAt: string
	expiresAt: string
	usedCount: number
	issueReason: string | null
	status: string
	createdAt: string
	couponMaster?: {
		couponMasterId: number
		couponType: string
		title: string
		image: string
		useLimitType: string
		maxUsesPerMember: number | null
	}
}

type PaymentRecord = {
	paymentId: number
	amount: number
	currency: string
	status: string
	paymentMethod: string
	completedAt: string | null
}

type EventHistoryRecord = {
	eventRegistrationId: number
	eventTitle: string | null
	startAt: string | null
	endAt: string | null
	status: string | null
	paymentStatus: string | null
	primaryParticipantCount: number
	companionCount: number
	expectedTotal: number | null
	attendedPrimaryCount: number
	attendedCompanionCount: number
	attendedTotal: number | null
	checkedIn: boolean
	createdAt: string | null
	cancelledAt: string | null
}

const DetailModal = (props: {
	publicSettings: Props['publicSettings']
	isMemberDetailModalVisible: boolean
	hideMemberDetailModal: () => void
	currentMember: Member
	memberAttributes: MemberAttribute[]
	handleMemberDelete: (member: Member) => void
	showMemberEditDetailModal: () => void
}) => {
	const {
		isMemberDetailModalVisible,
		hideMemberDetailModal,
		currentMember,
		memberAttributes,
		handleMemberDelete,
		showMemberEditDetailModal,
	} = props

	const [dataMember, setDataMember] = useState<
		Array<MemberAttribute & { value: string | number | true | Date }>
	>([])

	useEffect(() => {
		const dataMemberAttributes = memberAttributes?.map((item) => {
			const column = `memberAttributeId${item?.memberAttributeId}`
			return {
				...item,
				value: currentMember?.[column as keyof Member] || '',
			}
		})

		setDataMember(dataMemberAttributes)
	}, [memberAttributes, currentMember])

	const handleMemberAttributes = (
		item: MemberAttribute & { value: string | number | true | Date },
		index: number,
	) => {
		if (
			item?.type === 'text' ||
			item?.type === 'textarea' ||
			item?.type === 'url' ||
			item?.type === 'number_integer' ||
			item?.type === 'number_float' ||
			item?.type === 'radio' ||
			item?.type === 'time' ||
			item?.type === 'checkbox' ||
			item?.type === 'email' ||
			item?.type === 'telephone' ||
			item?.type === 'firstName' ||
			item?.type === 'lastName' ||
			item?.type === 'firstNameKana' ||
			item?.type === 'lastNameKana'
		) {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className='text-sm'>{item.value.toString() || 'ー'}</p>
				</Descriptions.Item>
			)
		}
		if (item?.type === 'address_postal') {
			let addressStr = '-'
			if (currentMember) {
				addressStr = COMMONS.generateMemberAttributeAddressStr({
					member: currentMember,
					memberAttributes,
					addrGroupSection: item.section,
				})
			}
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className='text-sm'>{addressStr}</p>
				</Descriptions.Item>
			)
		}
		if (item?.type === 'date') {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className='text-sm'>
						{item.value ? moment(item.value.toString()).format('YYYY年M月D日') : 'ー'}
					</p>
				</Descriptions.Item>
			)
		}
		if (item?.type === 'select') {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className='text-sm'>{item.value.toString() || 'ー'}</p>
				</Descriptions.Item>
			)
		}
		if (item?.type === 'datetime') {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<p className='text-sm'>
						{item.value
							? moment(item.value.toString()).format('YYYY年M月D日 HH:mm')
							: 'ー'}
					</p>
				</Descriptions.Item>
			)
		}
		if (item?.type === 'color') {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					<div
						style={{
							backgroundColor: item.value.toString(),
							width: '50px',
							height: '50px',
							border: '1px solid #000',
						}}
					></div>
				</Descriptions.Item>
			)
		}
		if (item?.type === 'image') {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					{item.value ? (
						<Image
							preview={true}
							className='w-20 h-20'
							src={
								API.GET_CUSTOM_REGISTRATION_UPLOAD_PATH(item.memberAttributeId) +
								`/${item.value}`
							}
						/>
					) : (
						<Image preview={true} className='w-20 h-20' src='/no-image.png' />
					)}
				</Descriptions.Item>
			)
		}
		if (item?.type === 'video') {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					{item.value ? (
						// biome-ignore lint/a11y/useMediaCaption: <just a video tag for preview, no need for caption>
						<video width='320' height='240' controls>
							<source
								src={`${API.GET_CUSTOM_REGISTRATION_UPLOAD_PATH(
									item.memberAttributeId,
								)}/${item.value}`}
							/>
							Your browser does not support the video tag.
						</video>
					) : (
						<p>動画がありません</p>
					)}
				</Descriptions.Item>
			)
		}
		if (item?.type === 'file') {
			return (
				<Descriptions.Item
					className='text-sm text-left'
					label={<p className='text-center'>{item?.label}</p>}
					key={`membershipItem${index}`}
				>
					{item.value ? (
						<a
							href={
								API.GET_CUSTOM_REGISTRATION_UPLOAD_PATH(item.memberAttributeId) +
								`/${item.value}`
							}
							download
						>
							{item.value.toString()}
						</a>
					) : (
						<p>ファイルがありません</p>
					)}
				</Descriptions.Item>
			)
		}
	}

	const memberDetailActions = [
		{
			key: '0',
			label: (
				<>
					<EditOutlined className='text-base' />
					<span className='text-base ml-2'>編集</span>
				</>
			),
			onClick: () => showMemberEditDetailModal(),
		},
		{
			key: '1',
			label: (
				<>
					<DeleteOutlined className='text-base' />
					<span className='text-base ml-2'>削除</span>
				</>
			),
			onClick: () => handleMemberDelete(currentMember),
		},
	]

	// Get extended member data
	const memberData = currentMember as any
	const visits = memberData?.visits || []
	const pointHistory = memberData?.points || []
	const coupons = memberData?.coupons || []
	const payments = memberData?.payments || []
	const eventHistory = memberData?.eventHistory || []
	const couponStats = memberData?.couponStats || {
		unused: 0,
		used: 0,
		expired: 0,
		total: 0,
	}

	// Visit history columns
	const visitColumns = [
		{
			title: '来館日',
			dataIndex: 'visitDate',
			key: 'visitDate',
			render: (date: string) => formatJstDate(date, 'YYYY年M月D日 HH:mm'),
		},
	]

	const pointColumns = [
		{
			title: '種別',
			dataIndex: 'source',
			key: 'source',
			render: (source: string | null) => getPointSourceLabel(source),
		},
		{
			title: '付与ポイント',
			dataIndex: 'point',
			key: 'point',
			render: (point: number) => `${point ?? 0}pt`,
		},
		{
			title: '残ポイント',
			dataIndex: 'remainingPoints',
			key: 'remainingPoints',
			render: (remainingPoints: number | null | undefined) => `${remainingPoints ?? 0}pt`,
		},
		{
			title: '有効期限',
			dataIndex: 'expiresAt',
			key: 'expiresAt',
			render: (date: string | null) => formatJstDate(date),
		},
		{
			title: '付与日',
			dataIndex: 'processedAt',
			key: 'processedAt',
			render: (processedAt: string | null, record: PointRecord) => {
				const date = processedAt || record?.createdAt
				return formatJstDate(date)
			},
		},
		{
			title: '作成日',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (date: string | null) => formatJstDate(date),
		},
		{
			title: '更新日',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			render: (date: string | null) => formatJstDate(date),
		},
	]

	// Coupon columns
	const couponColumns = [
		{
			title: 'クーポン種類',
			dataIndex: 'couponType',
			key: 'couponType',
			render: (_: unknown, record: CouponRecord) =>
				record.couponMaster?.title ||
				COUPON_UTILS.getCouponTypeLabel(record.couponMaster?.couponType),
		},
		{
			title: '使用回数',
			key: 'uses',
			render: (_: unknown, record: CouponRecord) => {
				const maxUses = record.couponMaster?.maxUsesPerMember
				const isUnlimited = record.couponMaster?.useLimitType === 'unlimited'
				const usedCount = record.usedCount || 0
				return `${usedCount} / ${isUnlimited ? '無制限' : maxUses || 1}`
			},
		},
		{
			title: '状態',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => (
				<Tag color={COUPON_UTILS.getCouponStatusColor(status)}>
					{COUPON_UTILS.getCouponStatusLabel(status)}
				</Tag>
			),
		},
		{
			title: '発行理由',
			dataIndex: 'issueReason',
			key: 'issueReason',
			render: (reason: string | null) => COUPON_UTILS.getCouponIssueReasonLabel(reason),
		},
		{
			title: '発行日',
			dataIndex: 'issuedAt',
			key: 'issuedAt',
			render: (date: string) => formatJstDate(date),
		},
		{
			title: '有効期限',
			dataIndex: 'expiresAt',
			key: 'expiresAt',
			render: (date: string) => formatJstDate(date),
		},
	]

	// Payment columns
	const paymentColumns = [
		{
			title: '金額',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount: number, record: PaymentRecord) =>
				`¥${amount?.toLocaleString() || 0} ${record.currency || ''}`,
		},
		{
			title: '支払方法',
			dataIndex: 'paymentMethod',
			key: 'paymentMethod',
			render: (method: string) => getPaymentMethodLabel(method),
		},
		{
			title: '状態',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => (
				<Tag color={getPaymentStatusColor(status)}>{getPaymentStatusLabel(status)}</Tag>
			),
		},
		{
			title: '完了日',
			dataIndex: 'completedAt',
			key: 'completedAt',
			render: (date: string) => formatJstDate(date),
		},
	]

	const eventHistoryColumns = [
		{
			title: 'イベント名',
			dataIndex: 'eventTitle',
			key: 'eventTitle',
			width: '26%',
			render: (eventTitle: string | null) => (
				<div
					style={{
						whiteSpace: 'normal',
						wordBreak: 'break-word',
						lineHeight: 1.45,
					}}
				>
					{eventTitle || 'ー'}
				</div>
			),
		},
		{
			title: '開催日時',
			key: 'eventDateTime',
			width: '17%',
			render: (_: unknown, record: EventHistoryRecord) => {
				if (!record?.startAt) {
					return 'ー'
				}
				const start = formatJstDate(record.startAt, 'YYYY年M月D日 HH:mm')
				const end = formatJstDate(record.endAt, 'HH:mm')
				return (
					<div style={{ lineHeight: 1.45 }}>
						<div>{start}</div>
						<div style={{ color: '#6b7280' }}>- {end}</div>
					</div>
				)
			},
		},
		{
			title: '予約状態',
			dataIndex: 'status',
			key: 'status',
			width: '10%',
			render: (status: string | null) => (
				<Tag color={getEventRegistrationStatusColor(status)}>
					{getEventRegistrationStatusLabel(status)}
				</Tag>
			),
		},
		{
			title: '決済状態',
			dataIndex: 'paymentStatus',
			key: 'paymentStatus',
			width: '10%',
			render: (paymentStatus: string | null) => (
				<Tag color={getEventPaymentStatusColor(paymentStatus)}>
					{getEventPaymentStatusLabel(paymentStatus)}
				</Tag>
			),
		},
		{
			title: '人数',
			key: 'participants',
			width: '13%',
			render: (_: unknown, record: EventHistoryRecord) => {
				const primary = record?.primaryParticipantCount ?? 0
				// TODO: reinstate companion display when needed
				// const companion = record?.companionCount ?? 0
				const expected = record?.expectedTotal ?? primary + (record?.companionCount ?? 0)
				return (
					<div style={{ lineHeight: 1.45 }}>
						<div>{expected}名</div>
						{/* <div style={{ color: '#6b7280' }}>
							本人{primary}/同伴{companion}
						</div> */}
					</div>
				)
			},
		},
		{
			title: '参加実績',
			key: 'attendance',
			width: '13%',
			render: (_: unknown, record: EventHistoryRecord) => {
				const attendedPrimary = record?.attendedPrimaryCount ?? 0
				// TODO: reinstate companion display when needed
				// const attendedCompanion = record?.attendedCompanionCount ?? 0
				const attendedTotal = record?.attendedTotal ?? attendedPrimary + (record?.attendedCompanionCount ?? 0)
				return (
					<div style={{ lineHeight: 1.45 }}>
						<div>{attendedTotal}名</div>
						{/* <div style={{ color: '#6b7280' }}>
							本人{attendedPrimary}/同伴{attendedCompanion}
						</div> */}
					</div>
				)
			},
		},
		{
			title: 'チェックイン',
			key: 'checkInStatus',
			width: '12%',
			render: (_: unknown, record: EventHistoryRecord) => {
				const checkedIn = record?.checkedIn === true
				const endAt = record?.endAt ?? null
				return (
					<Tag color={getCheckInStatusColor(checkedIn, endAt)}>
						{getCheckInStatusLabel(checkedIn, endAt)}
					</Tag>
				)
			},
		},
		{
			title: '予約日',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: '11%',
			render: (createdAt: string | null) => (
				<div style={{ lineHeight: 1.45, whiteSpace: 'nowrap' }}>
					{formatJstDate(createdAt, 'YYYY年M月D日 HH:mm')}
				</div>
			),
		},
		{
			title: 'キャンセル日時',
			dataIndex: 'cancelledAt',
			key: 'cancelledAt',
			width: '11%',
			render: (cancelledAt: string | null) => (
				<div style={{ lineHeight: 1.45, whiteSpace: 'nowrap' }}>
					{formatJstDate(cancelledAt, 'YYYY年M月D日 HH:mm')}
				</div>
			),
		},
	]

	const collapseItems = [
		{
			key: 'visits',
			label: (
				<span>
					<CalendarOutlined className='mr-2' />
					来館履歴 ({visits.length}件)
				</span>
			),
			children:
				visits.length > 0 ? (
					<Table
						dataSource={visits.slice(0, 10)}
						columns={visitColumns}
						rowKey='memberVisitId'
						size='small'
						pagination={false}
						tableLayout='fixed'
					/>
				) : (
					<Empty
						description='来館履歴がありません'
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				),
		},
		{
			key: 'events',
			label: (
				<span>
					<ScheduleOutlined className='mr-2' />
					イベント履歴 ({eventHistory.length}件)
				</span>
			),
			children:
				eventHistory.length > 0 ? (
					<Table
						dataSource={eventHistory}
						columns={eventHistoryColumns}
						rowKey='eventRegistrationId'
						size='small'
						pagination={false}
						tableLayout='auto'
						scroll={{ x: 1200 }}
					/>
				) : (
					<Empty
						description='イベント履歴がありません'
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				),
		},
		{
			key: 'points',
			label: (
				<span>
					<StarOutlined className='mr-2' />
					ポイント履歴 ({pointHistory.length}件)
				</span>
			),
			children:
				pointHistory.length > 0 ? (
					<Table
						dataSource={pointHistory}
						columns={pointColumns}
						rowKey='pointId'
						size='small'
						pagination={false}
						tableLayout='fixed'
					/>
				) : (
					<Empty
						description='ポイント履歴がありません'
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				),
		},
		{
			key: 'coupons',
			label: (
				<span>
					<GiftOutlined className='mr-2' />
					クーポン ({couponStats.unused}枚利用可能 / {couponStats.total}枚)
				</span>
			),
			children:
				coupons.length > 0 ? (
					<Table
						dataSource={coupons}
						columns={couponColumns}
						rowKey='couponId'
						size='small'
						pagination={false}
						tableLayout='fixed'
					/>
				) : (
					<Empty
						description='クーポンがありません'
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				),
		},
		{
			key: 'payments',
			label: (
				<span>
					<CreditCardOutlined className='mr-2' />
					支払履歴 ({payments.length}件)
				</span>
			),
			children:
				payments.length > 0 ? (
					<Table
						dataSource={payments}
						columns={paymentColumns}
						rowKey='paymentId'
						size='small'
						pagination={false}
						tableLayout='fixed'
					/>
				) : (
					<Empty
						description='支払履歴がありません'
						image={Empty.PRESENTED_IMAGE_SIMPLE}
					/>
				),
		},
	]

	const summaryCards = [
		{
			key: 'visits',
			icon: (
				<CalendarOutlined
					style={{
						color: '#22c55e',
						fontSize: '20px',
						marginBottom: '4px',
					}}
				/>
			),
			value: visits.length,
			valueColor: '#15803d',
			label: '来館履歴',
			backgroundColor: '#f0fdf4',
		},
		{
			key: 'events',
			icon: (
				<ScheduleOutlined
					style={{
						color: '#0ea5e9',
						fontSize: '20px',
						marginBottom: '4px',
					}}
				/>
			),
			value: eventHistory.length,
			valueColor: '#0369a1',
			label: 'イベント履歴',
			backgroundColor: '#f0f9ff',
		},
		{
			key: 'points',
			icon: (
				<StarOutlined
					style={{
						color: '#3b82f6',
						fontSize: '20px',
						marginBottom: '4px',
					}}
				/>
			),
			value: memberData?.visitPointBalance ?? memberData?.kakeruPoint ?? 0,
			valueColor: '#1d4ed8',
			label: 'ポイント',
			backgroundColor: '#eff6ff',
		},
		{
			key: 'coupons',
			icon: (
				<GiftOutlined
					style={{
						color: '#7c3aed',
						fontSize: '20px',
						marginBottom: '4px',
					}}
				/>
			),
			value: couponStats.unused || 0,
			valueColor: '#6d28d9',
			label: '利用可能クーポン',
			backgroundColor: '#f5f3ff',
		},
		{
			key: 'payments',
			icon: (
				<CreditCardOutlined
					style={{
						color: '#f97316',
						fontSize: '20px',
						marginBottom: '4px',
					}}
				/>
			),
			value: payments.length,
			valueColor: '#c2410c',
			label: '支払履歴',
			backgroundColor: '#fff7ed',
		},
	]

	return (
		<Modal
			title={
				<MemberActionsDiv className='flex justify-between'>
					<span>お客様詳細</span>
					<Dropdown
						menu={{ items: memberDetailActions }}
						trigger={['click']}
						placement='bottomRight'
					>
						<EllipsisOutlined />
					</Dropdown>
				</MemberActionsDiv>
			}
			open={isMemberDetailModalVisible}
			onCancel={hideMemberDetailModal}
			styles={{
				body: {
					maxHeight: '90vh',
					overflowY: 'auto',
					overflowX: 'visible',
				},
			}}
			footer={null}
			destroyOnClose
			centered
			width='92vw'
			style={{ maxWidth: 1200 }}
		>
			<motion.div
				className='flex flex-col justify-center'
				variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
				initial='hidden'
				animate='show'
				exit='hidden'
			>
				<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
					<div className='flex justify-center'>
						{currentMember?.picUrl ? (
							<Image
								preview={{
									mask: <EyeOutlined />,
									src: currentMember.picUrl,
									maskClassName: 'rounded-full',
								}}
								width={100}
								height={100}
								style={{
									border: `4px solid ${COMMONS.WHITE_COLOR}`,
								}}
								className='rounded-full'
								src={
									currentMember.picUrl.includes('randomuser')
										? currentMember.picUrl
										: `${currentMember.picUrl}/large`
								}
								fallback='/no-image.png'
							/>
						) : (
							<Image
								preview={false}
								width={100}
								height={100}
								style={{
									border: `4px solid ${COMMONS.WHITE_COLOR}`,
								}}
								className='rounded-full'
								src='/no-image.png'
							/>
						)}
					</div>
				</motion.div>
				<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
					<Descriptions column={1} layout='horizontal' bordered>
						<Descriptions.Item
							className='text-left'
							label={<p className='text-center'>ID</p>}
						>
							{currentMember?.memberId || 'ー'}
						</Descriptions.Item>
						<Descriptions.Item
							className='text-left'
							label={<p className='text-center'>会員コード</p>}
						>
							{currentMember?.memberCode || 'ー'}
						</Descriptions.Item>
						<Descriptions.Item
							className='text-left'
							label={<p className='text-center'>会員ステータス</p>}
						>
							<StatusBadge $status={memberData?.membershipStatus}>
								{getStatusLabel(memberData?.membershipStatus)}
							</StatusBadge>
						</Descriptions.Item>
						<Descriptions.Item
							className='text-left'
							label={<p className='text-center'>有効期限</p>}
						>
							{memberData?.activeUntil
								? moment(memberData.activeUntil).format('YYYY年M月D日')
								: 'ー'}
						</Descriptions.Item>
						<Descriptions.Item
							className='text-left'
							label={<p className='text-center'>LINE名</p>}
						>
							{currentMember?.displayName || 'ー'}
						</Descriptions.Item>
						{dataMember?.map((item, index) => handleMemberAttributes(item, index))}
						<Descriptions.Item
							className='text-left'
							label={<p className='text-center'>会員登録日</p>}
						>
							{currentMember?.memberSince
								? moment(currentMember.memberSince).format('YYYY年M月D日')
								: 'ー'}
						</Descriptions.Item>
					</Descriptions>
				</motion.div>

				{/* Activity Summary */}
				<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM} className='mt-4'>
					<div
						style={{
							display: 'flex',
							gap: '12px',
							justifyContent: 'center',
							flexWrap: 'wrap',
						}}
					>
						{summaryCards.map((card) => (
							<div
								key={card.key}
								style={{
									flex: '1 1 180px',
									maxWidth: '220px',
									backgroundColor: card.backgroundColor,
									borderRadius: '8px',
									padding: '12px',
									textAlign: 'center',
								}}
							>
								{card.icon}
								<p
									style={{
										fontSize: '18px',
										fontWeight: 'bold',
										color: card.valueColor,
										margin: 0,
									}}
								>
									{card.value}
								</p>
								<p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
									{card.label}
								</p>
							</div>
						))}
					</div>
				</motion.div>

				{/* History Sections */}
				<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM} className='mt-4'>
					<Collapse items={collapseItems} size='small' />
				</motion.div>

				<Divider />
				<motion.div
					className='flex justify-center'
					variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
				>
					<TapAnimationComponent>
						<Button size='large' className='px-12' onClick={hideMemberDetailModal}>
							閉じる
						</Button>
					</TapAnimationComponent>
				</motion.div>
			</motion.div>
		</Modal>
	)
}

export default DetailModal
